export type QueryRetry<TError = unknown> =
  | boolean
  | number
  | ((failureCount: number, error: TError) => boolean);

export type QueryDefaultOptions<TError = unknown> = {
  enabled?: boolean;
  refetchOnWindowFocus?: boolean;
  retry?: QueryRetry<TError>;
};

const NON_RETRYABLE_STATUS_CODES = new Set([400, 401, 404]);

const shouldRetryQuery = (_failureCount: number, error: unknown) => {
  if (typeof error !== "object" || error === null || !("response" in error)) {
    return true;
  }

  const response = error.response;
  if (
    typeof response !== "object" ||
    response === null ||
    !("status" in response)
  ) {
    return true;
  }

  return (
    typeof response.status !== "number" ||
    !NON_RETRYABLE_STATUS_CODES.has(response.status)
  );
};

/**
 * Applies the query defaults shared by the frontend and distribution clients.
 * Context-specific readiness is supplied by each client so this module stays
 * independent of React Query, authentication, and business-specific types.
 */
export const getDefaultQueryOptions = <
  TError = unknown,
  TOptions extends QueryDefaultOptions<TError> = QueryDefaultOptions<TError>,
>(
  overrideOptions?: TOptions,
  isContextReady = true,
): TOptions & {
  enabled: boolean;
  refetchOnWindowFocus: boolean;
  retry: QueryRetry<TError>;
} =>
  ({
    ...overrideOptions,
    enabled: (overrideOptions?.enabled ?? true) && isContextReady,
    refetchOnWindowFocus: overrideOptions?.refetchOnWindowFocus ?? true,
    retry: overrideOptions?.retry ?? shouldRetryQuery,
  }) as TOptions & {
    enabled: boolean;
    refetchOnWindowFocus: boolean;
    retry: QueryRetry<TError>;
  };
